import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'mc-theme';

const getSystemTheme = () => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * useTheme — manages light/dark mode for mc-frontend.
 *
 * Strategy:
 *  1. Check localStorage for a saved preference.
 *  2. Fall back to system preference.
 *  3. Apply by toggling the `dark` class on <html>.
 */
const useTheme = () => {
  const getInitialTheme = () => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
    return getSystemTheme();
  };

  const [theme, setThemeState] = useState(getInitialTheme);

  const applyTheme = useCallback((t) => {
    const root = document.documentElement;
    if (t === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const saved = localStorage.getItem(STORAGE_KEY);

      if (saved !== 'dark' && saved !== 'light') {
        setThemeState(mediaQuery.matches ? 'dark' : 'light');
      }
    };

    handleChange();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const setTheme = useCallback(
    (t) => {
      localStorage.setItem(STORAGE_KEY, t);
      setThemeState(t);
      applyTheme(t);
    },
    [applyTheme]
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const isDark = theme === 'dark';

  return { theme, isDark, setTheme, toggleTheme };
};

export default useTheme;
